import React, { useRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Swal from 'sweetalert2';
import {
  getDisallowedFilenameCharsMessage,
  hasDisallowedFilenameChars,
} from '../../../common/functions/file_name_validation.function';

const FileUploadSection = ({
  title,
  description,
  downloadLinkText,
  onFileChange,
  file,
  isLoading = false,
  loadingText = 'Uploading...',
}) => {
  const fileInputRef = useRef(null);

  const handleFileClick = () => {
    if (isLoading) {
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (hasDisallowedFilenameChars(selectedFile.name)) {
        Swal.fire({
          title: 'Invalid file name',
          text: `This file name contains unsupported characters. ${getDisallowedFilenameCharsMessage()}`,
          icon: 'error',
          confirmButtonColor: '#BD1D23',
          customClass: { popup: 'small-swal' },
        });
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      // Check if file is PDF
      if (
        selectedFile.type === 'application/pdf' ||
        selectedFile.name.toLowerCase().endsWith('.pdf')
      ) {
        onFileChange(selectedFile);
      } else {
        Swal.fire({
          title: 'Invalid file type',
          text: 'Please upload a PDF file only.',
          icon: 'error',
          confirmButtonColor: '#BD1D23',
          customClass: { popup: 'small-swal' },
        });
      }
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isLoading) {
      return;
    }
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (hasDisallowedFilenameChars(droppedFile.name)) {
        Swal.fire({
          title: 'Invalid file name',
          text: `This file name contains unsupported characters. ${getDisallowedFilenameCharsMessage()}`,
          icon: 'error',
          confirmButtonColor: '#BD1D23',
          customClass: { popup: 'small-swal' },
        });
        return;
      }
      // Check if file is PDF
      if (
        droppedFile.type === 'application/pdf' ||
        droppedFile.name.toLowerCase().endsWith('.pdf')
      ) {
        onFileChange(droppedFile);
      } else {
        Swal.fire({
          title: 'Invalid file type',
          text: 'Please upload a PDF file only.',
          icon: 'error',
          confirmButtonColor: '#BD1D23',
          customClass: { popup: 'small-swal' },
        });
      }
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Box
        onClick={handleFileClick}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          border: '2px dashed #667687ff',
          borderRadius: '4px',
          padding: '40px',
          textAlign: 'center',
          cursor: isLoading ? 'default' : 'pointer',
          backgroundColor: file ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
          transition: 'background-color 0.3s',
          '&:hover': {
            backgroundColor: isLoading ? undefined : 'rgba(25, 118, 210, 0.08)',
          },
        }}
      >
        {isLoading ? (
          <CircularProgress size={38} sx={{ color: '#BD1D23', mb: 1 }} />
        ) : (
          <AddIcon
            sx={{
              fontSize: '38px',
              color: '#BD1D23',
              mb: 1,
              padding: '5px',
              background: '#EBB9BB',
              borderRadius: '10px',
            }}
          />
        )}
        <Typography
          sx={{
            fontSize: '16px',
            fontFamily: 'Inter',
            fontWeight: 500,
            color: 'hsla(0, 0%, 0%, 0.85)',
            mb: 1,
          }}
        >
          {isLoading ? loadingText : description}
        </Typography>
        <Typography
          sx={{
            fontSize: '14px',
            fontFamily: 'Inter',
            color: '#666',
          }}
        >
          {downloadLinkText}{' '}
        </Typography>
        {file && (
          <Typography
            sx={{
              fontSize: '14px',
              fontFamily: 'Inter',
              color: '#1976d2',
              mt: 1,
            }}
          >
            Selected: {file.name}
          </Typography>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </Box>
    </Box>
  );
};

export default FileUploadSection;
